import { useState, useEffect } from 'react';
import type { WeatherSnapshot, WeatherApiResponse } from '@/types/weather';

interface UseWeatherResult {
  weather: WeatherSnapshot | null;
  error: string | null;
  loading: boolean;
}

export function useWeather(): UseWeatherResult {
  const [weather, setWeather] = useState<WeatherSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let source: EventSource | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const connect = () => {
      source?.close();
      source = new EventSource('/api/weather/stream');

      source.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data) as WeatherApiResponse;
          setWeather(payload.data);
          setError(null);
          setLoading(false);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to parse weather');
        }
      };

      source.onerror = () => {
        setError((prev) => prev ?? 'Weather stream disconnected — retrying…');
        setLoading(false);
        // If the browser has given up (CLOSED), recreate the stream ourselves.
        if (source?.readyState === EventSource.CLOSED) {
          reconnectTimer = setTimeout(connect, 2000);
        }
      };
    };

    const handleWake = () => {
      if (document.visibilityState !== 'visible') return;
      if (!source || source.readyState !== EventSource.OPEN) connect();
    };

    connect();
    document.addEventListener('visibilitychange', handleWake);
    window.addEventListener('online', handleWake);
    window.addEventListener('focus', handleWake);

    return () => {
      if (reconnectTimer) clearTimeout(reconnectTimer);
      document.removeEventListener('visibilitychange', handleWake);
      window.removeEventListener('online', handleWake);
      window.removeEventListener('focus', handleWake);
      source?.close();
    };
  }, []);

  return { weather, error, loading };
}
